import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";
import { Card } from "../ui/Card";
import { useRequest } from "../../hooks/useRequest";
import { useCarouselSelection } from "../../hooks/useCarouselSelection";
import {
  decideTaskHabitAchievement,
  getTaskInsights,
  getRewardsHistory,
  type TaskInsightsResponse,
  toggleTaskHabitAchievementMaintained,
  type HabitAchievementShelfItem,
  type MonthlyWrappedRecord,
  type RewardsHistorySummary,
  type WeeklyWrappedRecord,
} from "../../lib/api";
import { usePostLoginLanguage } from "../../i18n/postLoginLanguage";
import { emitHabitAchievementUpdated } from "../../lib/habitAchievementEvents";
import { subscribeToMediaQuery } from "../../lib/mediaQuery";
import { HabitAchievementSeal } from "./HabitAchievementSeal";
import { PreviewAchievementCard } from "./PreviewAchievementCard";
import {
  DASHBOARD_SEGMENTED_BUTTON_ACTIVE,
  DASHBOARD_SEGMENTED_BUTTON_BASE,
  DASHBOARD_SEGMENTED_BUTTON_INACTIVE,
  DASHBOARD_SEGMENTED_GROUP_BASE,
} from "./segmentedControlStyles";

const REWARDS_PILLAR_ORDER = [
  { code: "BODY", name: "Body" },
  { code: "MIND", name: "Mind" },
  { code: "SOUL", name: "Soul" },
] as const;

function scrollElementToLeft(element: HTMLElement, left: number, behavior: ScrollBehavior) {
  if (typeof element.scrollTo === "function") {
    element.scrollTo({ left, behavior });
    return;
  }
  element.scrollLeft = left;
}

type AchievementViewMode = "shelves" | "carousel";

interface RewardsSectionProps {
  userId: string;
  onOpenWeeklyWrapped?: (record?: WeeklyWrappedRecord | null) => void;
  initialData?: RewardsHistorySummary;
  onPendingCountChange?: (count: number) => void;
  disableRemote?: boolean;
  mockPreviewAchievementByTaskId?: Record<
    string,
    NonNullable<TaskInsightsResponse["previewAchievement"]>
  >;
  demoAnchors?: {
    shelves?: string;
    growthCalibration?: string;
    carouselStructure?: string;
    pillarSelector?: string;
    carouselTrack?: string;
    achievedCard?: string;
    achievedCardTaskId?: string;
    achievedCardFront?: string;
    achievedCardBack?: string;
    blockedCard?: string;
    blockedCardTaskId?: string;
    blockedCardFront?: string;
    blockedCardBack?: string;
    sealPath?: string;
    achievementFront?: string;
    achievementBack?: string;
    achievementCard?: string;
    weekly?: string;
    monthly?: string;
  };
  demoConfig?: {
    disableRemote?: boolean;
    forceAchievementsViewMode?: AchievementViewMode;
    mockPreviewAchievementByTaskId?: Record<
      string,
      NonNullable<TaskInsightsResponse["previewAchievement"]>
    >;
    anchors?: RewardsSectionProps["demoAnchors"];
    controls?: {
      onReady?: (controls: RewardsSectionDemoControls) => void;
      preventPageScrollOnProgrammaticFocus?: boolean;
    };
  };
  demoStepId?: string | null;
}

export type RewardsSectionDemoControls = {
  selectPillar: (pillarCode: "BODY" | "MIND" | "SOUL") => void;
  focusCarouselCard: (taskId: string) => void;
  openAchievedCard: () => void;
  openBlockedCard: () => void;
  focusBlockedCarouselCard: () => void;
  focusBlockedShelfCard: () => void;
  flipAchievementCard: () => void;
  ensureAchievementBackFace: () => void;
  closeAchievementOverlay: () => void;
  closeSealPathOverlay: () => void;
  closeAllOverlays: () => void;
};

export function RewardsSection({
  userId,
  onOpenWeeklyWrapped,
  initialData,
  onPendingCountChange,
  disableRemote = false,
  mockPreviewAchievementByTaskId,
  demoAnchors,
  demoConfig,
  demoStepId,
}: RewardsSectionProps) {
  const { language } = usePostLoginLanguage();
  const resolvedDisableRemote = demoConfig?.disableRemote ?? disableRemote;
  const forcedAchievementsViewMode = demoConfig?.forceAchievementsViewMode;
  const resolvedMockPreviewAchievementByTaskId =
    demoConfig?.mockPreviewAchievementByTaskId ??
    mockPreviewAchievementByTaskId;
  const resolvedDemoAnchors = demoConfig?.anchors ?? demoAnchors;

  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [decisionIndex, setDecisionIndex] = useState(0);
  const [celebrating, setCelebrating] = useState<
    null | HabitAchievementShelfItem[]
  >(null);
  const [educationBannerVisible, setEducationBannerVisible] = useState(false);
  const [isTransitioningDecision, setIsTransitioningDecision] = useState(false);
  const [isGrowthCalibrationModalOpen, setIsGrowthCalibrationModalOpen] =
    useState(false);
  const achievementsViewMode: AchievementViewMode =
    forcedAchievementsViewMode ?? "carousel";

  const { data, status, error, reload } = useRequest(
    () => getRewardsHistory(userId),
    [userId],
    {
      enabled: !resolvedDisableRemote && Boolean(userId),
      cacheKey: !resolvedDisableRemote && userId ? `rewards-history:${userId}` : null,
      staleMs: 2 * 60 * 1000,
    },
  );
  const effectiveData = data ?? initialData;

  const pendingItems = useMemo(
    () =>
      (effectiveData?.habitAchievements.achievedByPillar ?? [])
        .flatMap((group) => group.habits)
        .filter((habit) => habit.status === "pending_decision"),
    [effectiveData],
  );

  const pendingCount = effectiveData?.habitAchievements.pendingCount ?? 0;
  useEffect(() => {
    onPendingCountChange?.(pendingCount);
  }, [onPendingCountChange, pendingCount]);
  useEffect(() => {
    if (decisionIndex >= pendingItems.length) {
      setDecisionIndex(0);
    }
  }, [decisionIndex, pendingItems.length]);

  const weeklyItems = effectiveData?.weeklyWrapups ?? [];
  const monthlyItems = effectiveData?.monthlyWrapups ?? [];
  const growthCalibration = effectiveData?.growthCalibration ?? {
    countdownDays: getDaysUntilNextMonthWrapup(),
    latestPeriodLabel: null,
    summary: { up: 0, keep: 0, down: 0, total: 0 },
    latestResults: [],
  };

  const handleDecision = async (
    habit: HabitAchievementShelfItem,
    decision: "maintain" | "store",
  ) => {
    if (resolvedDisableRemote) {
      return;
    }
    await decideTaskHabitAchievement(habit.taskId, decision);
    emitHabitAchievementUpdated();
    const isLast = decisionIndex >= pendingItems.length - 1;
    if (isLast) {
      setCelebrating([...pendingItems]);
      setIsDecisionOpen(false);
      setDecisionIndex(0);
      setEducationBannerVisible(true);
      setTimeout(() => {
        setCelebrating(null);
      }, 2200);
    } else {
      setIsTransitioningDecision(true);
      window.setTimeout(() => {
        setDecisionIndex((value) => value + 1);
        setIsTransitioningDecision(false);
      }, 180);
    }
    await reload();
  };

  const handleOpenPendingDecision = async () => {
    if (resolvedDisableRemote) {
      return;
    }
    if (pendingItems.length > 0) {
      setDecisionIndex(0);
      setIsDecisionOpen(true);
      return;
    }
    await reload();
    setDecisionIndex(0);
    setIsDecisionOpen(true);
  };

  return (
    <Card
      rightSlot={
        <div className="flex items-center gap-2">
          {!resolvedDisableRemote ? (
            <a
              href={`/labs/logros?${new URLSearchParams({ lang: language, source: 'internal', returnTo: 'logros' }).toString()}`}
              title={
                language === "es"
                  ? "Ver demo guiada de Logros"
                  : "View guided Achievements demo"
              }
              aria-label={
                language === "es"
                  ? "Ver demo guiada de Logros"
                  : "View guided Achievements demo"
              }
              className="inline-flex items-center gap-1.5 rounded-full border border-violet-400/55 bg-violet-100/95 px-2.5 py-1 text-xs font-semibold text-violet-700 shadow-[0_4px_12px_rgba(124,58,237,0.16)] transition hover:border-violet-500/60 hover:bg-violet-200/90 hover:text-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400 disabled:cursor-not-allowed disabled:opacity-45 dark:border-violet-300/55 dark:bg-violet-500/35 dark:text-violet-50 dark:shadow-[0_6px_18px_rgba(124,58,237,0.35)] dark:hover:border-violet-200/80 dark:hover:bg-violet-500/45 dark:hover:text-white dark:focus-visible:outline-violet-200"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{language === "es" ? "Ver guía" : "View guide"}</span>
            </a>
          ) : null}
        </div>
      }
      bodyClassName="gap-5"
    >
      {!resolvedDisableRemote && pendingCount > 0 ? (
        <button
          type="button"
          onClick={() => {
            void handleOpenPendingDecision();
          }}
          className="ib-card-contour-shadow w-full rounded-2xl border border-amber-300/50 bg-gradient-to-r from-amber-400/15 via-orange-400/10 to-fuchsia-500/10 p-4 text-left"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-100">
            {language === "es" ? "Pendiente" : "Pending"}
          </p>
          <p className="mt-1 text-sm font-semibold text-[color:var(--color-text)]">
            {language === "es"
              ? "Tienes hábitos logrados para revisar"
              : "You have achieved habits to review"}
          </p>
        </button>
      ) : null}

      {!resolvedDisableRemote && educationBannerVisible ? (
        <div className="rounded-2xl border border-emerald-300/40 bg-emerald-400/10 p-4 text-sm text-emerald-100">
          {language === "es"
            ? "Tus hábitos logrados ahora viven en Logros. Puedes mantenerlos o guardarlos desde los estantes."
            : "Your achieved habits now live in Achievements. You can maintain or store them from the shelves."}
        </div>
      ) : null}

      {status === "error" ? (
        <p className="text-sm text-rose-200">
          {error?.message ??
            (language === "es"
              ? "Error al cargar Logros."
              : "Error loading Achievements.")}
        </p>
      ) : null}

      <AchievedShelf
        language={language}
        groups={effectiveData?.habitAchievements.achievedByPillar ?? []}
        demoAnchors={resolvedDemoAnchors}
        demoStepId={demoStepId}
        viewMode={achievementsViewMode}
        disableRemote={resolvedDisableRemote}
        mockPreviewAchievementByTaskId={resolvedMockPreviewAchievementByTaskId}
        onDemoControlsReady={demoConfig?.controls?.onReady}
        preventPageScrollOnProgrammaticFocus={
          demoConfig?.controls?.preventPageScrollOnProgrammaticFocus
        }
        onToggleMaintained={async (habit, enabled) => {
          if (resolvedDisableRemote) {
            return;
          }
          await toggleTaskHabitAchievementMaintained(habit.taskId, enabled);
          await reload();
        }}
      />

      <GrowthCalibrationShelf
        language={language}
        growthCalibration={growthCalibration}
        onOpenResults={() => setIsGrowthCalibrationModalOpen(true)}
        anchor={resolvedDemoAnchors?.growthCalibration}
      />

      <WeeklyWrapupShelf
        items={weeklyItems}
        onOpen={onOpenWeeklyWrapped}
        language={language}
        anchor={resolvedDemoAnchors?.weekly}
      />

      <MonthlyWrapupShelf
        items={monthlyItems}
        language={language}
        anchor={resolvedDemoAnchors?.monthly}
      />

      {!resolvedDisableRemote &&
      isDecisionOpen &&
      pendingItems[decisionIndex] ? (
        <DecisionModal
          language={language}
          currentIndex={decisionIndex}
          total={pendingItems.length}
          habit={pendingItems[decisionIndex]}
          transitioning={isTransitioningDecision}
          onClose={() => setIsDecisionOpen(false)}
          onMaintain={() =>
            handleDecision(pendingItems[decisionIndex], "maintain")
          }
          onStore={() => handleDecision(pendingItems[decisionIndex], "store")}
        />
      ) : null}

      {!resolvedDisableRemote && celebrating ? (
        <CelebrationOverlay
          language={language}
          habits={celebrating}
          onSkip={() => setCelebrating(null)}
        />
      ) : null}

      <GrowthCalibrationResultsModal
        language={language}
        isOpen={isGrowthCalibrationModalOpen}
        growthCalibration={growthCalibration}
        onClose={() => setIsGrowthCalibrationModalOpen(false)}
      />
    </Card>
  );
}

function GrowthCalibrationShelf({
  language,
  growthCalibration,
  onOpenResults,
  anchor,
}: {
  language: "es" | "en";
  growthCalibration: RewardsHistorySummary["growthCalibration"];
  onOpenResults: () => void;
  anchor?: string;
}) {
  const hasResults = growthCalibration.latestResults.length > 0;
  return (
    <div
      data-demo-anchor={anchor}
      className="ib-card-contour-shadow ib-light-elevated-surface ib-light-elevated-surface--rose rounded-2xl p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--color-text-dim)]">
            Growth Calibration Results
          </p>
          <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
            {language === "es"
              ? "Últimos ajustes automáticos de dificultad"
              : "Latest automatic difficulty adjustments"}
          </p>
        </div>
        <InlineCountdown
          days={growthCalibration.countdownDays}
          language={language}
        />
      </div>

      {hasResults ? (
        <button
          type="button"
          onClick={onOpenResults}
          className="mt-3 w-full rounded-xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-3 text-left transition hover:border-[color:var(--color-border-soft)] hover:bg-[color:var(--ib-surface-card-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-300"
        >
          <p className="text-lg font-bold tracking-[0.02em] text-[color:var(--color-text)] sm:text-xl">
            <span className="text-rose-300">
              ↑ {growthCalibration.summary.up}
            </span>
            <span className="mx-3 text-amber-300">
              • {growthCalibration.summary.keep}
            </span>
            <span className="text-emerald-300">
              ↓ {growthCalibration.summary.down}
            </span>
          </p>
          {growthCalibration.latestPeriodLabel ? (
            <p className="mt-1.5 text-xs uppercase tracking-[0.12em] text-[color:var(--color-slate-400)]">
              {growthCalibration.latestPeriodLabel}
            </p>
          ) : null}
        </button>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
          {language === "es"
            ? "Todavía no hay resultados de Growth Calibration. Tus próximos ajustes aparecerán aquí."
            : "There are no Growth Calibration results yet. Your next adjustments will appear here."}
        </div>
      )}
    </div>
  );
}

function MonthlyWrapupShelf({
  items,
  language,
  anchor,
}: {
  items: MonthlyWrappedRecord[];
  language: "es" | "en";
  anchor?: string;
}) {
  const monthlyCountdownDays = useDailyWrapupCountdown(
    getDaysUntilNextMonthWrapup,
  );
  const compactItems = useMemo(() => items.slice(0, 2), [items]);
  const latest = compactItems[0] ?? null;
  const previous = compactItems[1] ?? null;

  return (
    <div
      data-demo-anchor={anchor}
      className="ib-card-contour-shadow ib-light-elevated-surface ib-light-elevated-surface--indigo rounded-2xl p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--color-text-dim)]">
          {language === "es" ? "Monthly Wrapped" : "Monthly Wrapped"}
        </p>
        <InlineCountdown days={monthlyCountdownDays} language={language} />
      </div>
      {latest ? (
        <div className="mt-3 space-y-2">
          <div className="w-full rounded-xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-3 text-left">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--color-slate-400)]">
              {latest.periodKey}
            </p>
            <CompletionDots
              completionDays={latest.completionDays ?? []}
              range={resolveMonthRange(latest.periodKey)}
              language={language}
            />
          </div>
          {previous ? (
            <div className="w-full rounded-xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-3 text-left">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--color-slate-400)]">
                {previous.periodKey}
              </p>
              <p className="mt-1 text-xs font-semibold text-[color:var(--color-text-muted)]">
                {language === "es" ? "Periodo anterior" : "Previous period"}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
          {language === "es"
            ? "Aún no tienes Wrap-Ups mensuales. Tus próximos resúmenes aparecerán aquí."
            : "You don't have monthly wrap-ups yet. Your next summaries will appear here."}
        </div>
      )}
    </div>
  );
}

function WeeklyWrapupShelf({
  items,
  onOpen,
  language,
  anchor,
}: {
  items: WeeklyWrappedRecord[];
  onOpen?: (record?: WeeklyWrappedRecord | null) => void;
  language: "es" | "en";
  anchor?: string;
}) {
  const weeklyCountdownDays = useDailyWrapupCountdown(
    getDaysUntilNextWeeklyWrapup,
  );
  const compactItems = useMemo(() => items.slice(0, 2), [items]);
  return (
    <div
      data-demo-anchor={anchor}
      className="ib-card-contour-shadow ib-light-elevated-surface ib-light-elevated-surface--emerald rounded-2xl p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--color-text-dim)]">
          {language === "es" ? "Weekly Wrapped" : "Weekly Wrapped"}
        </p>
        <InlineCountdown days={weeklyCountdownDays} language={language} />
      </div>
      {compactItems.length ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {compactItems.map((item) => {
            const weeklyEmotion =
              item.payload.emotions.weekly ?? item.payload.emotions.biweekly;
            const dominantPillar = item.payload.summary.pillarDominant;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen?.(item)}
                className="rounded-xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-3 text-left"
              >
                <p className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--color-slate-400)]">
                  {item.weekStart} → {item.weekEnd}
                </p>
                <div className="mt-2 flex min-h-6 items-center gap-2.5">
                  <EmotionDot color={weeklyEmotion?.color} />
                  <span
                    className="text-[1.3rem] leading-none"
                    aria-label={
                      language === "es"
                        ? "Pilar dominante en GP"
                        : "Dominant pillar by GP"
                    }
                  >
                    {resolvePillarEmoji(dominantPillar)}
                  </span>
                  <CompletionDots
                    completionDays={item.completionDays ?? []}
                    range={{ start: item.weekStart, end: item.weekEnd }}
                    language={language}
                    inline
                  />
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
          {language === "es"
            ? "Aún no tienes Weekly Wrap-Ups. Tus próximos resúmenes semanales aparecerán aquí."
            : "You don't have weekly wrap-ups yet. Your next weekly summaries will appear here."}
        </div>
      )}
    </div>
  );
}

function InlineCountdown({
  days,
  language,
}: {
  days: number;
  language: "es" | "en";
}) {
  return (
    <p className="whitespace-nowrap text-right text-xs font-semibold tracking-[0.03em] text-[color:var(--color-text-dim)]">
      {language === "es" ? "Próximo en " : "Next in "}
      <span className="text-lg font-black leading-none text-[color:var(--color-text-strong)]">
        {days}d
      </span>
    </p>
  );
}

function GrowthCalibrationResultsModal({
  language,
  isOpen,
  growthCalibration,
  onClose,
}: {
  language: "es" | "en";
  isOpen: boolean;
  growthCalibration: RewardsHistorySummary["growthCalibration"];
  onClose: () => void;
}) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const resultTone: Record<"up" | "keep" | "down", string> = {
    up: "text-rose-300",
    keep: "text-amber-300",
    down: "text-emerald-300",
  };
  const resultSymbol: Record<"up" | "keep" | "down", string> = {
    up: "↑",
    keep: "•",
    down: "↓",
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[240] flex items-end justify-center bg-slate-950/75 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl overflow-hidden rounded-3xl border border-[color:var(--color-border-subtle)] bg-[color:var(--color-surface-elevated)] shadow-[0_24px_70px_rgba(0,0,0,0.45)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[color:var(--color-border-subtle)] px-4 py-3 sm:px-5">
          <div>
            <p className="text-sm font-semibold text-[color:var(--color-text-strong)]">
              {language === "es"
                ? "Resultados de Growth Calibration"
                : "Growth Calibration Results"}
            </p>
            {growthCalibration.latestPeriodLabel ? (
              <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-slate-400)]">
                {growthCalibration.latestPeriodLabel}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[color:var(--color-border-subtle)] px-2 py-1 text-xs"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[70vh] overflow-auto p-3 sm:p-4">
          {growthCalibration.latestResults.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
              {language === "es"
                ? "Todavía no hay resultados de Growth Calibration. Tus próximos ajustes aparecerán aquí."
                : "There are no Growth Calibration results yet. Your next adjustments will appear here."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--color-text-dim)]">
                  <tr>
                    <th className="px-2 py-2">
                      {language === "es" ? "Tarea" : "Task"}
                    </th>
                    <th className="px-2 py-2">
                      {language === "es" ? "Resultado" : "Result"}
                    </th>
                    <th className="px-2 py-2">
                      {language === "es" ? "Progreso" : "Progress"}
                    </th>
                    <th className="px-2 py-2">
                      {language === "es" ? "Tasa" : "Rate"}
                    </th>
                    <th className="px-2 py-2">
                      {language === "es" ? "Detalle" : "Detail"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {growthCalibration.latestResults.map((row) => (
                    <tr
                      key={`${row.taskId}-${row.evaluatedAt}`}
                      className="border-t border-[color:var(--color-border-subtle)] align-top"
                    >
                      <td className="px-2 py-2">
                        <p className="font-semibold text-[color:var(--color-text)]">
                          {row.taskTitle}
                        </p>
                        {row.difficultyBefore || row.difficultyAfter ? (
                          <p className="mt-0.5 text-[11px] text-[color:var(--color-text-muted)]">
                            {row.difficultyBefore ?? "—"} →{" "}
                            {row.difficultyAfter ?? "—"}
                          </p>
                        ) : null}
                      </td>
                      <td
                        className={`px-2 py-2 text-base font-bold ${resultTone[row.finalAction]}`}
                      >
                        {resultSymbol[row.finalAction]}
                      </td>
                      <td className="px-2 py-2 text-[color:var(--color-text)]">
                        {row.actualCompletions} / {row.expectedTarget}
                      </td>
                      <td className="px-2 py-2 text-[color:var(--color-text)]">
                        {Math.round(row.completionRatePct)}%
                      </td>
                      <td className="px-2 py-2 text-[color:var(--color-text)]">
                        <p>{row.reason}</p>
                        {row.clampApplied && row.clampReason ? (
                          <p className="mt-1 text-[11px] text-[color:var(--color-text-muted)]">
                            {row.clampReason}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function EmotionDot({ color }: { color?: string }) {
  const dotColor =
    color && color.trim().length ? color : "rgba(255,255,255,0.45)";
  return (
    <span
      className="inline-flex h-4 w-4 shrink-0 rounded-full shadow-[0_0_10px_rgba(255,255,255,0.2)]"
      style={{ backgroundColor: dotColor }}
      aria-label="dominant-emotion-dot"
    />
  );
}

function resolvePillarEmoji(pillar: string | null | undefined): string {
  if (!pillar) return "•";
  const normalized = pillar.toString().trim().toUpperCase();
  if (normalized === "BODY" || normalized === "CUERPO") return "🫀";
  if (normalized === "MIND" || normalized === "MENTE") return "🧠";
  if (normalized === "SOUL" || normalized === "ALMA") return "🏵️";
  return "•";
}

function resolvePillarHeader(
  pillar: { code?: string | null; name?: string | null },
  language: "es" | "en",
): string {
  const code = (pillar.code ?? "").trim().toUpperCase();
  if (code === "BODY") return `${language === "es" ? "Cuerpo" : "Body"} 🫀`;
  if (code === "MIND") return `${language === "es" ? "Mente" : "Mind"} 🧠`;
  if (code === "SOUL") return `${language === "es" ? "Alma" : "Soul"} 🏵️`;
  return `${pillar.name ?? "Pillar"} ${resolvePillarEmoji(pillar.code)}`;
}

function CompletionDots({
  completionDays,
  range,
  language,
  inline = false,
}: {
  completionDays: string[];
  range: { start: string; end: string };
  language: "es" | "en";
  inline?: boolean;
}) {
  const dayLabels =
    language === "es"
      ? ["L", "M", "X", "J", "V", "S", "D"]
      : ["M", "T", "W", "T", "F", "S", "S"];
  const completed = useMemo(() => new Set(completionDays), [completionDays]);
  const weekDates = useMemo(
    () => getWeekDatesFromRange(range),
    [range.end, range.start],
  );
  return (
    <div
      className={`flex items-center justify-center gap-1.5 ${inline ? "w-auto mt-0 min-w-0 flex-1 justify-start" : "mt-3 w-full"}`}
    >
      {weekDates.map((dateKey, index) => {
        const isDone = completed.has(dateKey);
        return (
          <div
            key={`${dateKey}-${index}`}
            className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-semibold ${
              isDone
                ? "border-[#d8b4fe] bg-[#e9d5ff] text-[#7c3aed] shadow-sm dark:border-violet-300/50 dark:bg-violet-500/45 dark:text-violet-100"
                : "border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] text-[color:var(--color-text-muted)]"
            }`}
          >
            {dayLabels[index]}
          </div>
        );
      })}
    </div>
  );
}

function getWeekDatesFromRange(range: {
  start: string;
  end: string;
}): string[] {
  const start = new Date(`${range.start}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) {
    return [];
  }
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setUTCDate(start.getUTCDate() + index);
    return date.toISOString().slice(0, 10);
  });
}

function resolveMonthRange(periodKey: string): { start: string; end: string } {
  const [yearRaw, monthRaw] = periodKey.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return { start: periodKey, end: periodKey };
  }
  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month, 0));
  const rangeStart = new Date(monthEnd);
  rangeStart.setUTCDate(monthEnd.getUTCDate() - 6);
  if (rangeStart < monthStart) {
    return {
      start: monthStart.toISOString().slice(0, 10),
      end: monthEnd.toISOString().slice(0, 10),
    };
  }
  return {
    start: rangeStart.toISOString().slice(0, 10),
    end: monthEnd.toISOString().slice(0, 10),
  };
}

function getDaysUntilNextWeeklyWrapup(referenceDate = new Date()): number {
  const nowUtc = new Date(
    Date.UTC(
      referenceDate.getUTCFullYear(),
      referenceDate.getUTCMonth(),
      referenceDate.getUTCDate(),
    ),
  );
  const dayOfWeek = nowUtc.getUTCDay();
  const daysToNextMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  return Math.max(0, daysToNextMonday);
}

function getDaysUntilNextMonthWrapup(referenceDate = new Date()): number {
  const nowUtc = new Date(
    Date.UTC(
      referenceDate.getUTCFullYear(),
      referenceDate.getUTCMonth(),
      referenceDate.getUTCDate(),
    ),
  );
  const nextMonthStart = new Date(
    Date.UTC(nowUtc.getUTCFullYear(), nowUtc.getUTCMonth() + 1, 1),
  );
  const diffMs = nextMonthStart.getTime() - nowUtc.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function useDailyWrapupCountdown(
  getter: (referenceDate?: Date) => number,
): number {
  const [days, setDays] = useState(() => getter(new Date()));

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const refresh = () => setDays(getter(new Date()));
    refresh();

    const now = new Date();
    const nextUtcMidnight = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0,
        0,
        3,
      ),
    );
    const firstTickDelay = Math.max(
      5_000,
      nextUtcMidnight.getTime() - now.getTime(),
    );
    let intervalId: number | null = null;

    const timeoutId = window.setTimeout(() => {
      refresh();
      intervalId = window.setInterval(refresh, 24 * 60 * 60 * 1000);
    }, firstTickDelay);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
    };
  }, [getter]);

  return days;
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();

    return subscribeToMediaQuery(mediaQuery, update);
  }, []);

  return prefersReducedMotion;
}

function AchievedShelf({
  groups,
  language,
  viewMode,
  onToggleMaintained,
  disableRemote,
  mockPreviewAchievementByTaskId,
  demoAnchors,
  onDemoControlsReady,
  preventPageScrollOnProgrammaticFocus,
  demoStepId,
}: {
  groups: RewardsHistorySummary["habitAchievements"]["achievedByPillar"];
  language: "es" | "en";
  viewMode: AchievementViewMode;
  onToggleMaintained: (
    habit: HabitAchievementShelfItem,
    enabled: boolean,
  ) => Promise<void>;
  disableRemote: boolean;
  mockPreviewAchievementByTaskId?: Record<
    string,
    NonNullable<TaskInsightsResponse["previewAchievement"]>
  >;
  demoAnchors?: RewardsSectionProps["demoAnchors"];
  onDemoControlsReady?: (controls: RewardsSectionDemoControls) => void;
  preventPageScrollOnProgrammaticFocus?: boolean;
  demoStepId?: string | null;
}) {
  const [activeHabitId, setActiveHabitId] = useState<string | null>(null);
  const [previewHabit, setPreviewHabit] =
    useState<HabitAchievementShelfItem | null>(null);
  const [showBackFace, setShowBackFace] = useState(false);
  const [activePillarCode, setActivePillarCode] = useState<
    (typeof REWARDS_PILLAR_ORDER)[number]["code"]
  >(REWARDS_PILLAR_ORDER[0].code);
  const [flippedCarouselHabitId, setFlippedCarouselHabitId] = useState<
    string | null
  >(null);
  const [maintainPendingHabitId, setMaintainPendingHabitId] = useState<
    string | null
  >(null);
  const [isGridView, setIsGridView] = useState(false);
  const [gridHabitId, setGridHabitId] = useState<string | null>(null);
  const [gridShowBack, setGridShowBack] = useState(false);
  const gridScrollTopRef = useRef(0);
  const gridTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const gridFlipTimerRef = useRef<number | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const normalizedGroups = useMemo(() => {
    const byCode = new Map(
      groups.map((group) => [group.pillar.code.toUpperCase(), group]),
    );
    return REWARDS_PILLAR_ORDER.map((pillar) => {
      const existing = byCode.get(pillar.code);
      return {
        pillar: existing?.pillar ?? {
          id: null,
          code: pillar.code,
          name: pillar.name,
        },
        habits: existing?.habits ?? [],
      };
    });
  }, [groups]);

  const habitsById = useMemo(() => {
    const map = new Map<string, HabitAchievementShelfItem>();
    normalizedGroups.forEach((group) => {
      group.habits.forEach((habit) => {
        map.set(habit.id, habit);
      });
    });
    return map;
  }, [normalizedGroups]);

  const activeHabit = activeHabitId
    ? (habitsById.get(activeHabitId) ?? null)
    : null;
  const activePillarHabits = useMemo(() => {
    const group = normalizedGroups.find(
      (entry) => entry.pillar.code.toUpperCase() === activePillarCode,
    );
    return group?.habits ?? [];
  }, [activePillarCode, normalizedGroups]);
  const {
    activeIndex: activeCarouselIndex,
    setActiveIndex: setActiveCarouselIndex,
    trackRef: carouselTrackRef,
    handleTrackScroll: handleCarouselTrackScroll,
  } = useCarouselSelection<HTMLDivElement>({
    itemAttribute: "data-achievement-carousel-index",
    initialIndex: 0,
  });
  const resolvedAchievedTaskId = demoAnchors?.achievedCardTaskId;
  const resolvedBlockedTaskId = demoAnchors?.blockedCardTaskId;
  const isShelfFocusStep = demoStepId === "logros-shelves";
  const isDemoExperience = Boolean(demoStepId);
  const useCompactLockedPreview = demoStepId?.startsWith("logros-") ?? false;
  const isCarouselView = viewMode === "carousel";

  const closeGridDetail = useCallback(() => {
    if (gridFlipTimerRef.current != null) {
      window.clearTimeout(gridFlipTimerRef.current);
      gridFlipTimerRef.current = null;
    }
    const closingId = gridHabitId;
    setGridShowBack(false);
    window.setTimeout(() => {
      setGridHabitId(null);
      window.scrollTo({ top: gridScrollTopRef.current, behavior: "auto" });
      gridTriggerRefs.current.get(closingId ?? "")?.focus({ preventScroll: true });
    }, prefersReducedMotion ? 0 : 260);
  }, [gridHabitId, prefersReducedMotion]);

  const closeGrid = useCallback(() => {
    if (gridHabitId) {
      closeGridDetail();
      return;
    }
    setIsGridView(false);
  }, [closeGridDetail, gridHabitId]);

  const openGridDetail = useCallback((habit: HabitAchievementShelfItem) => {
    if (gridHabitId) return;
    gridScrollTopRef.current = window.scrollY;
    setGridHabitId(habit.id);
    setGridShowBack(prefersReducedMotion);
    if (!prefersReducedMotion) {
      gridFlipTimerRef.current = window.setTimeout(() => {
        setGridShowBack(true);
        gridFlipTimerRef.current = null;
      }, 420);
    }
  }, [gridHabitId, prefersReducedMotion]);

  useEffect(() => {
    if (!isGridView) return;
    const previousOverflow = document.body.style.overflowX;
    document.body.style.overflowX = "hidden";
    const onPopState = () => closeGrid();
    window.addEventListener("popstate", onPopState);
    return () => {
      document.body.style.overflowX = previousOverflow;
      window.removeEventListener("popstate", onPopState);
    };
  }, [closeGrid, isGridView]);

  useEffect(() => {
    if (!gridHabitId) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeGridDetail();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeGridDetail, gridHabitId]);

  useEffect(() => () => {
    if (gridFlipTimerRef.current != null) window.clearTimeout(gridFlipTimerRef.current);
  }, []);

  useEffect(() => {
    if (viewMode !== "shelves") {
      setActiveHabitId(null);
      setPreviewHabit(null);
      setShowBackFace(false);
    }
  }, [viewMode]);

  useEffect(() => {
    setFlippedCarouselHabitId(null);
    setActiveCarouselIndex(0);
    if (carouselTrackRef.current) {
      scrollElementToLeft(carouselTrackRef.current, 0, "auto");
    }
  }, [activePillarCode, carouselTrackRef, setActiveCarouselIndex]);

  useEffect(() => {
    if (!flippedCarouselHabitId) {
      return;
    }
    const flippedIndex = activePillarHabits.findIndex(
      (habit) => habit.id === flippedCarouselHabitId,
    );
    if (flippedIndex !== activeCarouselIndex) {
      setFlippedCarouselHabitId(null);
    }
  }, [activeCarouselIndex, activePillarHabits, flippedCarouselHabitId]);

  const focusBlockedShelfCard = useCallback(() => {
    const target = document.querySelector(
      '[data-demo-anchor="logros-blocked-card"]',
    ) as HTMLElement | null;
    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center",
    });
    const horizontalContainer = target.closest(
      ".ib-rewards-shelf-scroll",
    ) as HTMLElement | null;
    if (horizontalContainer) {
      const itemLeft = target.offsetLeft;
      const centeredLeft =
        itemLeft - (horizontalContainer.clientWidth - target.clientWidth) / 2;
      scrollElementToLeft(horizontalContainer, Math.max(0, centeredLeft), "auto");
    }
  }, []);

  const scrollCarouselToIndex = useCallback(
    (targetIndex: number) => {
      const track = carouselTrackRef.current;
      if (!track) {
        return;
      }
      const clampedIndex = Math.max(
        0,
        Math.min(targetIndex, Math.max(activePillarHabits.length - 1, 0)),
      );
      const targetCard = track.querySelector<HTMLElement>(
        `[data-achievement-carousel-index="${clampedIndex}"]`,
      );
      if (!targetCard) {
        return;
      }
      const behavior = prefersReducedMotion ? "auto" : "smooth";
      if (preventPageScrollOnProgrammaticFocus) {
        const centeredLeft =
          targetCard.offsetLeft - (track.clientWidth - targetCard.clientWidth) / 2;
        scrollElementToLeft(track, Math.max(0, centeredLeft), behavior);
      } else if (typeof targetCard.scrollIntoView === "function") {
        targetCard.scrollIntoView({
          behavior,
          inline: "center",
          block: "nearest",
        });
      }
      setActiveCarouselIndex(clampedIndex);
    },
    [
      activePillarHabits.length,
      carouselTrackRef,
      prefersReducedMotion,
      preventPageScrollOnProgrammaticFocus,
      setActiveCarouselIndex,
    ],
  );

  const resolveHabitLocationByTaskId = useCallback(
    (taskId: string) => {
      for (const group of normalizedGroups) {
        const habitIndex = group.habits.findIndex(
          (habit) => habit.taskId === taskId,
        );
        if (habitIndex >= 0) {
          return {
            pillarCode: (group.pillar.code?.toUpperCase() ??
              "BODY") as (typeof REWARDS_PILLAR_ORDER)[number]["code"],
            index: habitIndex,
            habit: group.habits[habitIndex],
          };
        }
      }
      return null;
    },
    [normalizedGroups],
  );

  const focusCarouselCardByTaskId = useCallback(
    (taskId: string) => {
      const location = resolveHabitLocationByTaskId(taskId);
      if (!location) {
        return;
      }
      setActiveHabitId(null);
      setPreviewHabit(null);
      setShowBackFace(false);
      setFlippedCarouselHabitId(null);
      setActivePillarCode(location.pillarCode);
      window.setTimeout(() => {
        scrollCarouselToIndex(location.index);
      }, 40);
    },
    [resolveHabitLocationByTaskId, scrollCarouselToIndex],
  );

  const openAchievedCard = useCallback(() => {
    if (isCarouselView) {
      if (!resolvedAchievedTaskId) {
        return;
      }
      focusCarouselCardByTaskId(resolvedAchievedTaskId);
      return;
    }
    const target = normalizedGroups
      .flatMap((group) => group.habits)
      .find(
        (habit) =>
          habit.taskId === resolvedAchievedTaskId &&
          habit.status !== "not_achieved",
      );
    if (!target) {
      return;
    }
    setPreviewHabit(null);
    setShowBackFace(false);
    setActiveHabitId(target.id);
  }, [
    focusCarouselCardByTaskId,
    isCarouselView,
    normalizedGroups,
    resolvedAchievedTaskId,
  ]);

  const openBlockedCard = useCallback(() => {
    if (isCarouselView) {
      if (!resolvedBlockedTaskId) {
        return;
      }
      const location = resolveHabitLocationByTaskId(resolvedBlockedTaskId);
      if (!location) {
        return;
      }
      setActiveHabitId(null);
      setShowBackFace(false);
      setPreviewHabit(null);
      setFlippedCarouselHabitId(null);
      setActivePillarCode(location.pillarCode);
      window.setTimeout(() => {
        scrollCarouselToIndex(location.index);
        window.setTimeout(
          () => {
            setFlippedCarouselHabitId(location.habit.id);
          },
          prefersReducedMotion ? 0 : 120,
        );
      }, 40);
      return;
    }
    const target = normalizedGroups
      .flatMap((group) => group.habits)
      .find(
        (habit) =>
          habit.taskId === resolvedBlockedTaskId &&
          habit.status === "not_achieved",
      );
    if (!target) {
      return;
    }
    setActiveHabitId(null);
    setShowBackFace(false);
    setPreviewHabit(target);
  }, [
    isCarouselView,
    normalizedGroups,
    prefersReducedMotion,
    resolveHabitLocationByTaskId,
    resolvedBlockedTaskId,
    scrollCarouselToIndex,
  ]);

  useEffect(() => {
    onDemoControlsReady?.({
      selectPillar: (pillarCode) => {
        setActiveHabitId(null);
        setPreviewHabit(null);
        setShowBackFace(false);
        setFlippedCarouselHabitId(null);
        setActivePillarCode(pillarCode);
        window.setTimeout(() => {
          scrollCarouselToIndex(0);
        }, 40);
      },
      focusCarouselCard: (taskId) => {
        focusCarouselCardByTaskId(taskId);
      },
      openAchievedCard,
      openBlockedCard,
      focusBlockedCarouselCard: () => {
        if (!resolvedBlockedTaskId) {
          return;
        }
        focusCarouselCardByTaskId(resolvedBlockedTaskId);
      },
      focusBlockedShelfCard,
      flipAchievementCard: () => {
        if (isCarouselView) {
          if (!resolvedAchievedTaskId) {
            return;
          }
          const location = resolveHabitLocationByTaskId(resolvedAchievedTaskId);
          if (!location) {
            return;
          }
          setActivePillarCode(location.pillarCode);
          scrollCarouselToIndex(location.index);
          window.setTimeout(
            () => {
              setFlippedCarouselHabitId((current) =>
                current === location.habit.id ? null : location.habit.id,
              );
            },
            prefersReducedMotion ? 0 : 120,
          );
          return;
        }
        if (activeHabitId) {
          setShowBackFace((current) => !current);
          return;
        }
        openAchievedCard();
        window.setTimeout(() => {
          setShowBackFace(true);
        }, 80);
      },
      ensureAchievementBackFace: () => {
        if (isCarouselView) {
          if (!resolvedAchievedTaskId) {
            return;
          }
          const location = resolveHabitLocationByTaskId(resolvedAchievedTaskId);
          if (!location) {
            return;
          }
          setActivePillarCode(location.pillarCode);
          scrollCarouselToIndex(location.index);
          window.setTimeout(
            () => {
              setFlippedCarouselHabitId(location.habit.id);
            },
            prefersReducedMotion ? 0 : 120,
          );
          return;
        }
        if (activeHabitId) {
          setShowBackFace(true);
          return;
        }
        openAchievedCard();
        window.setTimeout(() => {
          setShowBackFace(true);
        }, 80);
      },
      closeAchievementOverlay: () => {
        setActiveHabitId(null);
        setShowBackFace(false);
      },
      closeSealPathOverlay: () => setPreviewHabit(null),
      closeAllOverlays: () => {
        setActiveHabitId(null);
        setShowBackFace(false);
        setPreviewHabit(null);
        setFlippedCarouselHabitId(null);
      },
    });
  }, [
    activeHabitId,
    focusBlockedShelfCard,
    focusCarouselCardByTaskId,
    isCarouselView,
    onDemoControlsReady,
    openAchievedCard,
    openBlockedCard,
    prefersReducedMotion,
    resolveHabitLocationByTaskId,
    resolvedAchievedTaskId,
    resolvedBlockedTaskId,
    scrollCarouselToIndex,
  ]);

  const getSealBadge = (habit: HabitAchievementShelfItem) => {
    const pillarCode = (habit.pillar ?? "X").slice(0, 1).toUpperCase();
    const traitCode = habit.trait?.code?.slice(0, 3).toUpperCase() ?? "---";
    return `${pillarCode}-${traitCode}`;
  };

  const handleCarouselCardClick = useCallback(
    (habitId: string, index: number) => {
      if (index !== activeCarouselIndex) {
        setFlippedCarouselHabitId(null);
        scrollCarouselToIndex(index);
        window.setTimeout(
          () => {
            setFlippedCarouselHabitId((current) =>
              current === habitId ? null : habitId,
            );
          },
          prefersReducedMotion ? 0 : 120,
        );
        return;
      }
      setFlippedCarouselHabitId((current) =>
        current === habitId ? null : habitId,
      );
    },
    [activeCarouselIndex, prefersReducedMotion, scrollCarouselToIndex],
  );

  const handleToggleMaintained = useCallback(
    async (habit: HabitAchievementShelfItem, enabled: boolean) => {
      setMaintainPendingHabitId(habit.id);
      try {
        await onToggleMaintained(habit, enabled);
      } finally {
        setMaintainPendingHabitId(null);
      }
    },
    [onToggleMaintained],
  );

  const pillarChipLabels: Record<
    (typeof REWARDS_PILLAR_ORDER)[number]["code"],
    string
  > = {
    BODY: language === "es" ? "Cuerpo" : "Body",
    MIND: language === "es" ? "Mente" : "Mind",
    SOUL: language === "es" ? "Alma" : "Soul",
  };
  const pillarChipIcons: Record<
    (typeof REWARDS_PILLAR_ORDER)[number]["code"],
    string
  > = {
    BODY: "🫀",
    MIND: "🧠",
    SOUL: "🏵️",
  };

  return (
    <LayoutGroup id="all-achievements">
    <div className="space-y-3 overflow-x-clip" data-demo-anchor={demoAnchors?.shelves}>
      <div className="flex min-h-11 items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-[color:var(--color-text-strong)]">
          {isGridView
            ? language === "es" ? "Todos los logros" : "All Achievements"
            : language === "es" ? "Hábitos logrados" : "Achieved habits"}
        </h2>
        {isGridView ? (
          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] px-3 text-xs font-semibold text-[color:var(--color-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400"
            aria-label={language === "es" ? "Volver a Logros" : "Back to Achievements"}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="m15 18-6-6 6-6" /></svg>
            {language === "es" ? "Volver" : "Back"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              window.history.pushState({ innerbloomAchievementsGrid: true }, "");
              setIsGridView(true);
            }}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] text-[color:var(--color-text)] transition hover:bg-[color:var(--ib-surface-card-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400"
            aria-label={language === "es" ? "Abrir vista en grilla de todos los logros" : "Open all achievements grid view"}
            aria-pressed={isGridView}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          </button>
        )}
      </div>
      {!isCarouselView && isShelfFocusStep ? (
        <div
          data-demo-anchor="logros-shelves-pillars"
          className="rounded-2xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-3 shadow-[0_16px_32px_rgba(0,0,0,0.2)] ring-1 ring-[color:var(--color-accent-primary)]/35"
        >
          <div className="grid grid-cols-3 gap-2">
            {normalizedGroups.map((group) => (
              <div
                key={`${group.pillar.code}-demo-column`}
                className="rounded-xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] px-2 py-3 text-center"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--color-text)]">
                  {resolvePillarHeader(group.pillar, language)}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {isGridView ? (
        <div className="space-y-7 pb-[calc(env(safe-area-inset-bottom)+5rem)]" data-achievement-view="grid">
          {normalizedGroups.map((group) => {
            const achievedCount = group.habits.filter((habit) => habit.status !== "not_achieved").length;
            return (
              <section key={`grid-${group.pillar.code}`} className="space-y-3" aria-labelledby={`achievement-grid-${group.pillar.code}`}>
                <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border-subtle)] pb-2">
                  <h3 id={`achievement-grid-${group.pillar.code}`} className="text-sm font-semibold text-[color:var(--color-text-strong)]">
                    {resolvePillarHeader(group.pillar, language)}
                  </h3>
                  <p className="shrink-0 text-[11px] font-semibold text-[color:var(--color-text-dim)]">
                    {achievedCount} / {group.habits.length} {language === "es" ? "logrados" : "achieved"}
                  </p>
                </div>
                {group.habits.length ? (
                  <div className="grid grid-cols-3 gap-x-2 gap-y-4 min-[370px]:grid-cols-4">
                    {group.habits.map((habit) => {
                      const achieved = habit.status !== "not_achieved";
                      return (
                        <button
                          key={habit.id}
                          ref={(node) => { if (node) gridTriggerRefs.current.set(habit.id, node); else gridTriggerRefs.current.delete(habit.id); }}
                          type="button"
                          onClick={() => {
                            window.history.pushState({ innerbloomAchievementDetail: habit.id }, "");
                            openGridDetail(habit);
                          }}
                          disabled={Boolean(gridHabitId)}
                          aria-label={`${habit.taskName}, ${achieved ? (language === "es" ? "logrado" : "achieved") : (language === "es" ? "no logrado" : "not achieved")}`}
                          className="group flex min-h-24 min-w-0 flex-col items-center justify-start rounded-xl px-1 py-1 text-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400 disabled:pointer-events-none"
                        >
                          <motion.span layoutId={`achievement-${habit.id}`} className="flex h-[4.25rem] w-[4.25rem] items-center justify-center min-[390px]:h-[4.75rem] min-[390px]:w-[4.75rem]">
                            <HabitAchievementSeal
                              pillar={habit.pillar ?? group.pillar.code}
                              traitCode={habit.trait?.code}
                              traitName={habit.trait?.name}
                              alt={`${habit.taskName} seal`}
                              disabled={!achieved}
                              className={`h-full w-full overflow-hidden rounded-full ${achieved ? "" : "opacity-55"}`}
                              imgClassName="h-full w-full object-contain"
                              fallback={<span className="text-xs font-semibold text-[color:var(--color-text-dim)]">{getSealBadge(habit)}</span>}
                            />
                          </motion.span>
                          <span className="mt-1 line-clamp-2 w-full text-[11px] font-medium leading-tight text-[color:var(--color-text-muted)]">{habit.taskName}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="py-4 text-sm text-[color:var(--color-text-muted)]">{language === "es" ? "Sin tareas seguidas en este pilar todavía." : "No tracked tasks in this pillar yet."}</p>
                )}
              </section>
            );
          })}
        </div>
      ) : isCarouselView ? (
        <div
          className="space-y-3"
          data-demo-anchor={demoAnchors?.carouselStructure}
        >
          <div
            className={DASHBOARD_SEGMENTED_GROUP_BASE}
            role="tablist"
            aria-label={
              language === "es" ? "Seleccionar pilar" : "Select pillar"
            }
            data-demo-anchor={demoAnchors?.pillarSelector}
          >
            {REWARDS_PILLAR_ORDER.map((pillar) => {
              const isSelected = activePillarCode === pillar.code;
              return (
                <button
                  key={pillar.code}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => setActivePillarCode(pillar.code)}
                  className={`${DASHBOARD_SEGMENTED_BUTTON_BASE} px-2 py-1.5 ${
                    isSelected
                      ? DASHBOARD_SEGMENTED_BUTTON_ACTIVE
                      : DASHBOARD_SEGMENTED_BUTTON_INACTIVE
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden>{pillarChipIcons[pillar.code]}</span>
                    <span>{pillarChipLabels[pillar.code]}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {activePillarHabits.length > 0 ? (
            <>
              <div
                ref={carouselTrackRef}
                onScroll={handleCarouselTrackScroll}
                className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-1 pb-1 sm:gap-3 sm:px-2 lg:gap-4 lg:px-4 xl:px-5"
                data-demo-anchor={demoAnchors?.carouselTrack}
              >
                {activePillarHabits.map((habit, index) => {
                  const isFlipped =
                    flippedCarouselHabitId === habit.id &&
                    activeCarouselIndex === index;
                  const isAchieved = habit.status !== "not_achieved";
                  const slotLabel = getSealBadge(habit);
                  const cardStatusLabel = isAchieved
                    ? language === "es"
                      ? "hábito logrado"
                      : "achieved habit"
                    : language === "es"
                      ? "hábito bloqueado"
                      : "locked habit";
                  return (
                    <div
                      key={habit.id}
                      role="button"
                      tabIndex={0}
                      data-achievement-carousel-index={index}
                      data-demo-anchor={
                        demoAnchors?.achievedCardTaskId === habit.taskId
                          ? demoAnchors?.achievedCard
                          : demoAnchors?.blockedCardTaskId === habit.taskId
                            ? demoAnchors?.blockedCard
                            : undefined
                      }
                      onClick={() => handleCarouselCardClick(habit.id, index)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleCarouselCardClick(habit.id, index);
                        }
                      }}
                      aria-pressed={isFlipped}
                      aria-label={`${habit.taskName}, ${cardStatusLabel}`}
                      className="relative h-[27.5rem] w-[86%] shrink-0 snap-center bg-transparent p-0 text-left [perspective:1000px] sm:h-[24rem] sm:w-[22.5rem] lg:h-[29rem] lg:w-[calc((100%-2rem)/3)] lg:max-w-[25rem] lg:snap-start xl:h-[30rem]"
                    >
                      <div
                        className={`relative h-full w-full transition-transform ease-[cubic-bezier(0.2,0.82,0.2,1)] [transform-style:preserve-3d] ${
                          prefersReducedMotion ? "duration-0" : "duration-700"
                        } ${isFlipped ? "[transform:rotateY(180deg)]" : ""}`}
                      >
                        <div
                          aria-hidden={isFlipped}
                          className={`ib-card-contour-shadow absolute inset-0 flex h-full flex-col overflow-hidden rounded-[1.9rem] border p-3.5 text-center [backface-visibility:hidden] sm:p-4 lg:p-5 ${
                            isAchieved
                              ? activeCarouselIndex === index
                                ? "border-amber-400/80 bg-[color:var(--color-surface-elevated)] shadow-[0_0_0_1px_rgba(251,191,36,0.16),0_20px_42px_rgba(2,8,23,0.16)] dark:bg-[rgba(7,13,16,0.78)] dark:shadow-[0_0_0_1px_rgba(251,191,36,0.16),0_22px_44px_rgba(0,0,0,0.26)]"
                                : "border-[color:var(--color-border-soft)] bg-[color:var(--color-surface-elevated)] shadow-[0_16px_30px_rgba(2,8,23,0.12)] dark:bg-[rgba(7,13,16,0.62)] dark:shadow-[0_16px_30px_rgba(2,8,23,0.32)]"
                              : "border-dashed border-[color:var(--color-border-strong)] bg-[color:var(--ib-surface-card)] shadow-[0_12px_24px_rgba(2,8,23,0.1)] dark:border-[color:var(--color-border-subtle)] dark:bg-[rgba(7,13,16,0.58)] dark:shadow-[0_12px_24px_rgba(2,8,23,0.22)]"
                          }`}
                          data-demo-anchor={
                            demoAnchors?.achievedCardTaskId === habit.taskId
                              ? demoAnchors?.achievedCardFront
                              : demoAnchors?.blockedCardTaskId === habit.taskId
                                ? demoAnchors?.blockedCardFront
                                : undefined
                          }
                        >
                          {!isAchieved ? (
                            <span className="absolute right-0 top-0 z-20 rounded-full border border-amber-300/65 bg-amber-200/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-900 shadow-[0_8px_18px_rgba(180,83,9,0.24)] dark:border-amber-300/35 dark:bg-[color:var(--ib-surface-card)] dark:text-[color:var(--color-text-dim)]">
                              {language === "es" ? "Bloqueado" : "Locked"}
                            </span>
                          ) : null}
                          <div className="flex min-h-0 flex-1 items-start justify-center pt-1 sm:pt-2">
                            <HabitAchievementSeal
                              pillar={habit.pillar ?? activePillarCode}
                              traitCode={habit.trait?.code}
                              traitName={habit.trait?.name}
                              alt={`${habit.taskName} seal`}
                              disabled={!isAchieved}
                              className={`h-[17rem] min-h-[17rem] w-[17rem] min-w-[17rem] overflow-hidden rounded-[2rem] border border-transparent bg-transparent shadow-none sm:h-[15.5rem] sm:min-h-[15.5rem] sm:w-[15.5rem] sm:min-w-[15.5rem] lg:h-[16.8rem] lg:min-h-[16.8rem] lg:w-[16.8rem] lg:min-w-[16.8rem] ${isAchieved ? "" : "opacity-55"}`}
                              imgClassName="h-full w-full object-contain drop-shadow-[0_22px_26px_rgba(15,23,42,0.22)]"
                              fallback={
                                <span className="text-6xl font-semibold leading-none text-[color:var(--color-text-muted)] sm:text-7xl">
                                  {isAchieved
                                    ? habit.seal.visible
                                      ? "🏅"
                                      : slotLabel
                                    : slotLabel}
                                </span>
                              }
                            />
                          </div>
                          <div className="mt-auto space-y-2 pb-2">
                            <p className="text-xl font-semibold leading-tight text-[color:var(--color-text-strong)]">
                              {habit.taskName}
                            </p>
                            <p className="line-clamp-1 text-base font-medium text-[color:var(--color-text-muted)]">
                              {habit.trait?.name ??
                                (language === "es"
                                  ? "Rasgo en progreso"
                                  : "Trait in progress")}
                            </p>
                            {isAchieved ? (
                              <span className="mx-auto inline-flex w-fit rotate-[-2deg] rounded-full border border-emerald-400/45 bg-emerald-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,0.12)] dark:text-emerald-200">
                                {language === "es"
                                  ? "Hábito logrado"
                                  : "Achieved habit"}
                              </span>
                            ) : (
                              <p className="mx-auto inline-flex max-w-[14rem] items-center justify-center rounded-full border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card-active)] px-3 py-1 text-[11px] font-semibold text-[color:var(--color-text-subtle)]">
                                {language === "es"
                                  ? "Toca para ver el reverso"
                                  : "Tap to see the back"}
                              </p>
                            )}
                          </div>
                        </div>

                        <div
                          aria-hidden={!isFlipped}
                          className={`ib-card-contour-shadow absolute inset-0 flex h-full flex-col overflow-hidden rounded-[1.9rem] border p-3.5 text-left shadow-[0_18px_38px_rgba(2,8,23,0.18)] [backface-visibility:hidden] [transform:rotateY(180deg)] sm:p-4 lg:p-5 ${
                            isAchieved
                              ? "border-amber-400/85 bg-[radial-gradient(circle_at_50%_18%,rgba(251,191,36,0.12),transparent_46%),var(--color-surface-elevated)] dark:bg-[radial-gradient(circle_at_50%_18%,rgba(251,191,36,0.16),transparent_46%),rgba(7,13,16,0.84)]"
                              : "border-[color:var(--color-border-soft)] bg-[color:var(--color-surface-elevated)] dark:bg-[rgba(7,13,16,0.84)]"
                          }`}
                          data-demo-anchor={
                            isAchieved
                              ? demoAnchors?.achievedCardBack
                              : demoAnchors?.blockedCardBack
                          }
                        >
                          {isFlipped ? (
                            isAchieved ? (
                              <>
                                <AchievedHabitBackContent
                                  habit={habit}
                                  language={language}
                                  disableRemote={disableRemote}
                                  maintainPendingHabitId={maintainPendingHabitId}
                                  onToggleMaintainedWithPending={
                                    handleToggleMaintained
                                  }
                                  compact
                                />
                                <p className="mt-auto text-xs text-[color:var(--color-text-dim)]">
                                  {language === "es"
                                    ? "Toca otra vez para volver al frente"
                                    : "Tap again to return to front"}
                                </p>
                              </>
                            ) : (
                              <>
                                <div className="space-y-0.5 sm:space-y-0">
                                  <p className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--color-text-dim)]">
                                    {language === "es"
                                      ? "Logro bloqueado"
                                      : "Achievement locked"}
                                  </p>
                                  <h3 className="text-[15px] font-semibold leading-[1.2] text-[color:var(--color-text-strong)] sm:text-base">
                                    {habit.taskName}
                                  </h3>
                                </div>
                                <div className="min-h-0 flex-1">
                                  <LockedAchievementHabitDevelopment
                                    habit={habit}
                                    language={language}
                                    disableRemote={disableRemote}
                                    mockPreviewAchievementByTaskId={
                                      mockPreviewAchievementByTaskId
                                    }
                                    loadOnVisible={isFlipped}
                                    compact={useCompactLockedPreview}
                                    embedded
                                  />
                                </div>
                              </>
                            )
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => scrollCarouselToIndex(activeCarouselIndex - 1)}
                  disabled={activeCarouselIndex <= 0}
                  className="rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] px-3 py-1 text-xs font-semibold text-[color:var(--color-text)] shadow-[0_6px_14px_rgba(15,23,42,0.08)] transition hover:bg-[color:var(--ib-surface-card-hover)] disabled:opacity-50"
                >
                  {language === "es" ? "Anterior" : "Previous"}
                </button>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--color-text-dim)]">
                  {Math.min(activeCarouselIndex + 1, activePillarHabits.length)}{" "}
                  / {activePillarHabits.length}
                </p>
                <button
                  type="button"
                  onClick={() => scrollCarouselToIndex(activeCarouselIndex + 1)}
                  disabled={
                    activeCarouselIndex >= activePillarHabits.length - 1
                  }
                  className="rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] px-3 py-1 text-xs font-semibold text-[color:var(--color-text)] shadow-[0_6px_14px_rgba(15,23,42,0.08)] transition hover:bg-[color:var(--ib-surface-card-hover)] disabled:opacity-50"
                >
                  {language === "es" ? "Siguiente" : "Next"}
                </button>
              </div>
            </>
          ) : (
            <p className="rounded-2xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
              {language === "es"
                ? "Sin tareas seguidas en este pilar todavía."
                : "No tracked tasks in this pillar yet."}
            </p>
          )}
        </div>
      ) : (
        normalizedGroups.map((group) => (
          <section
            key={group.pillar.code}
            className={`space-y-2 transition ${isShelfFocusStep ? "space-y-1.5" : ""}`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--color-text-dim)]">
              {resolvePillarHeader(group.pillar, language)}
            </p>
            <div
              className={`ib-rewards-shelf-scroll flex overflow-x-auto pb-1 transition ${isShelfFocusStep ? "gap-2.5" : "gap-3"} ${isDemoExperience ? "pt-0.5" : ""}`}
            >
              {group.habits.map((habit) => {
                const isAchieved = habit.status !== "not_achieved";
                const active = habit.id === activeHabitId;
                const traitCode =
                  habit.trait?.code?.slice(0, 3).toUpperCase() ?? "---";
                const slotLabel = `${(habit.pillar ?? group.pillar.code ?? "X").slice(0, 1).toUpperCase()}-${traitCode}`;

                if (!isAchieved) {
                  const blockedAnchor =
                    demoAnchors?.blockedCardTaskId === habit.taskId
                      ? demoAnchors?.blockedCard
                      : undefined;
                  return (
                    <button
                      key={habit.id}
                      type="button"
                      data-demo-anchor={blockedAnchor}
                      onClick={() => setPreviewHabit(habit)}
                      className={`flex shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] px-3 text-center opacity-80 transition hover:border-[color:var(--color-border-strong)] hover:opacity-100 ${isShelfFocusStep ? "h-32 w-24 py-3" : "h-40 w-32 py-4"}`}
                    >
                      <HabitAchievementSeal
                        pillar={habit.pillar ?? group.pillar.code}
                        traitCode={habit.trait?.code}
                        traitName={habit.trait?.name}
                        alt={`${habit.taskName} seal`}
                        disabled
                        className={`flex items-center justify-center overflow-hidden rounded-full border border-dashed border-[color:var(--color-border-subtle)] bg-transparent ${isShelfFocusStep ? "h-16 min-h-16 w-16 min-w-16 max-h-16 max-w-16" : "h-20 min-h-20 w-20 min-w-20 max-h-20 max-w-20"}`}
                        imgClassName="h-full w-full object-cover"
                        fallback={
                          <div className="flex h-full w-full items-center justify-center text-xs font-semibold tracking-[0.12em] text-[color:var(--color-text-dim)]">
                            {slotLabel}
                          </div>
                        }
                      />
                      <p
                        className={`mt-2 w-full truncate font-semibold text-[color:var(--color-text-muted)] ${isShelfFocusStep ? "text-xs" : "text-sm"}`}
                      >
                        {habit.taskName}
                      </p>
                    </button>
                  );
                }

                const achievedAnchor =
                  demoAnchors?.achievedCardTaskId === habit.taskId
                    ? demoAnchors?.achievedCard
                    : undefined;
                return (
                  <button
                    key={habit.id}
                    type="button"
                    data-demo-anchor={achievedAnchor}
                    onClick={() => {
                      setActiveHabitId(habit.id);
                      setShowBackFace(false);
                    }}
                    className={`flex shrink-0 flex-col items-center justify-center rounded-2xl border px-3 text-center transition ${isShelfFocusStep ? "h-32 w-24 py-3" : "h-40 w-32 py-4"} ${active ? "border-violet-300/60 bg-violet-500/10" : "border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] hover:border-[color:var(--color-border-strong)]"}`}
                  >
                    <HabitAchievementSeal
                      pillar={habit.pillar ?? group.pillar.code}
                      traitCode={habit.trait?.code}
                      traitName={habit.trait?.name}
                      alt={`${habit.taskName} seal`}
                      className={`flex items-center justify-center overflow-hidden rounded-full border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] shadow-[0_10px_30px_rgba(0,0,0,0.22)] ${isShelfFocusStep ? "h-16 min-h-16 w-16 min-w-16 max-h-16 max-w-16" : "h-20 min-h-20 w-20 min-w-20 max-h-20 max-w-20"}`}
                      imgClassName="h-full w-full object-cover"
                      fallback={
                        <span className="text-3xl leading-none">
                          {habit.seal.visible ? "🏅" : getSealBadge(habit)}
                        </span>
                      }
                    />
                    <p
                      className={`mt-2 w-full truncate font-semibold text-[color:var(--color-text)] ${isShelfFocusStep ? "text-xs" : "text-sm"}`}
                    >
                      {habit.taskName}
                    </p>
                  </button>
                );
              })}
              {group.habits.length === 0 ? (
                <p className="py-6 text-sm text-[color:var(--color-text-muted)]">
                  {language === "es"
                    ? "Sin tareas seguidas en este pilar todavía."
                    : "No tracked tasks in this pillar yet."}
                </p>
              ) : null}
            </div>
          </section>
        ))
      )}

      {!isCarouselView ? (
        <>
          <AchievementFocusOverlay
            habit={activeHabit}
            language={language}
            showBackFace={showBackFace}
            disableRemote={disableRemote}
            demoAnchors={demoAnchors}
            onFlip={() => setShowBackFace((current) => !current)}
            onClose={() => {
              setActiveHabitId(null);
              setShowBackFace(false);
            }}
            onToggleMaintainedWithPending={handleToggleMaintained}
            maintainPendingHabitId={maintainPendingHabitId}
          />

          <NotAchievedPreviewOverlay
            habit={previewHabit}
            language={language}
            disableRemote={disableRemote}
            mockPreviewAchievementByTaskId={mockPreviewAchievementByTaskId}
            demoAnchors={demoAnchors}
            compact={useCompactLockedPreview}
            onClose={() => setPreviewHabit(null)}
          />
        </>
      ) : null}
      <AnimatePresence>
        {isGridView && gridHabitId && habitsById.get(gridHabitId) ? (
          <AchievementGridOverlay
            key={gridHabitId}
            habit={habitsById.get(gridHabitId)!}
            language={language}
            showBack={gridShowBack}
            prefersReducedMotion={prefersReducedMotion}
            disableRemote={disableRemote}
            mockPreviewAchievementByTaskId={mockPreviewAchievementByTaskId}
            maintainPendingHabitId={maintainPendingHabitId}
            onToggleMaintained={handleToggleMaintained}
            onClose={() => window.history.back()}
          />
        ) : null}
      </AnimatePresence>
    </div>
    </LayoutGroup>
  );
}

function AchievementGridOverlay({
  habit,
  language,
  showBack,
  prefersReducedMotion,
  disableRemote,
  mockPreviewAchievementByTaskId,
  maintainPendingHabitId,
  onToggleMaintained,
  onClose,
}: {
  habit: HabitAchievementShelfItem;
  language: "es" | "en";
  showBack: boolean;
  prefersReducedMotion: boolean;
  disableRemote: boolean;
  mockPreviewAchievementByTaskId?: Record<string, NonNullable<TaskInsightsResponse["previewAchievement"]>>;
  maintainPendingHabitId: string | null;
  onToggleMaintained: (habit: HabitAchievementShelfItem, enabled: boolean) => Promise<void>;
  onClose: () => void;
}) {
  if (typeof document === "undefined") return null;
  const achieved = habit.status !== "not_achieved";

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[230] flex items-center justify-center overflow-hidden bg-slate-950/70 px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-[calc(env(safe-area-inset-top)+1rem)]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: prefersReducedMotion ? 0.1 : 0.25 }}
      onClick={onClose}
      data-achievement-overlay="grid-card"
    >
      <motion.div
        layoutId={`achievement-${habit.id}`}
        className="relative h-[min(78dvh,30rem)] min-h-[22rem] w-full max-w-sm [perspective:1200px]"
        transition={prefersReducedMotion ? { duration: 0.1 } : { type: "spring", stiffness: 240, damping: 27 }}
        onClick={(event) => { event.stopPropagation(); onClose(); }}
      >
        <motion.div
          className="relative h-full w-full [transform-style:preserve-3d]"
          animate={{ rotateY: showBack || prefersReducedMotion ? 180 : 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.65, ease: [0.2, 0.82, 0.2, 1] }}
          data-achievement-grid-flip={showBack ? "back" : "front"}
        >
          <div className={`ib-card-contour-shadow absolute inset-0 flex flex-col items-center justify-center gap-4 overflow-hidden rounded-[1.9rem] border bg-[color:var(--color-surface-elevated)] p-5 text-center [backface-visibility:hidden] ${achieved ? "border-amber-400/80" : "border-dashed border-[color:var(--color-border-strong)]"}`}>
            <HabitAchievementSeal
              pillar={habit.pillar}
              traitCode={habit.trait?.code}
              traitName={habit.trait?.name}
              alt={`${habit.taskName} seal`}
              disabled={!achieved}
              className={`h-[min(60vw,16rem)] w-[min(60vw,16rem)] overflow-hidden rounded-full ${achieved ? "" : "opacity-55"}`}
              imgClassName="h-full w-full object-contain"
              fallback={<span className="text-4xl text-[color:var(--color-text-muted)]">{(habit.pillar ?? "X").slice(0, 1)}-{habit.trait?.code?.slice(0, 3) ?? "---"}</span>}
            />
            <p className="text-lg font-semibold text-[color:var(--color-text-strong)]">{habit.taskName}</p>
          </div>
          <div className={`ib-card-contour-shadow absolute inset-0 flex h-full flex-col overflow-hidden rounded-[1.9rem] border p-4 text-left [backface-visibility:hidden] [transform:rotateY(180deg)] ${achieved ? "border-amber-400/85 bg-[radial-gradient(circle_at_50%_18%,rgba(251,191,36,0.12),transparent_46%),var(--color-surface-elevated)]" : "border-[color:var(--color-border-soft)] bg-[color:var(--color-surface-elevated)]"}`}>
            {showBack || prefersReducedMotion ? achieved ? (
              <>
                <AchievedHabitBackContent habit={habit} language={language} disableRemote={disableRemote} maintainPendingHabitId={maintainPendingHabitId} onToggleMaintainedWithPending={onToggleMaintained} compact />
                <p className="mt-auto text-xs text-[color:var(--color-text-dim)]">{language === "es" ? "Toca para volver" : "Tap to go back"}</p>
              </>
            ) : (
              <>
                <div className="space-y-0.5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--color-text-dim)]">{language === "es" ? "Logro bloqueado" : "Achievement locked"}</p>
                  <h3 className="text-base font-semibold text-[color:var(--color-text-strong)]">{habit.taskName}</h3>
                </div>
                <div className="min-h-0 flex-1">
                  <LockedAchievementHabitDevelopment habit={habit} language={language} disableRemote={disableRemote} mockPreviewAchievementByTaskId={mockPreviewAchievementByTaskId} loadOnVisible compact embedded />
                </div>
                <p className="text-xs text-[color:var(--color-text-dim)]">{language === "es" ? "Toca para volver" : "Tap to go back"}</p>
              </>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

function LockedAchievementHabitDevelopment({
  habit,
  language,
  disableRemote,
  mockPreviewAchievementByTaskId,
  loadOnVisible,
  compact = false,
  embedded = false,
}: {
  habit: HabitAchievementShelfItem;
  language: "es" | "en";
  disableRemote: boolean;
  mockPreviewAchievementByTaskId?: Record<
    string,
    NonNullable<TaskInsightsResponse["previewAchievement"]>
  >;
  loadOnVisible: boolean;
  compact?: boolean;
  embedded?: boolean;
}) {
  const taskId = habit.taskId;
  const mockPreviewAchievement =
    mockPreviewAchievementByTaskId?.[taskId] ?? null;
  const { data, status, error } = useRequest(
    () => getTaskInsights(taskId),
    [taskId],
    {
      enabled: loadOnVisible && !disableRemote && !mockPreviewAchievement,
      cacheKey: loadOnVisible && !disableRemote && !mockPreviewAchievement ? `task-insights:${taskId}` : null,
      staleMs: 5 * 60 * 1000,
    },
  );
  const previewAchievement =
    mockPreviewAchievement ?? data?.previewAchievement ?? null;
  const showLoading =
    loadOnVisible && status === "loading" && !mockPreviewAchievement;
  const showError = loadOnVisible && status === "error";
  const showEmpty =
    loadOnVisible && !showLoading && !showError && !previewAchievement;
  if (showLoading) {
    return (
      <p className="rounded-xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-2.5 text-xs text-[color:var(--color-text-muted)]">
        {language === "es"
          ? "Cargando desarrollo del hábito…"
          : "Loading habit development…"}
      </p>
    );
  }

  if (showError) {
    return (
      <p className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-2.5 text-xs text-rose-100">
        {error?.message ??
          (language === "es"
            ? "No pudimos cargar el desarrollo del hábito."
            : "We couldn't load habit development.")}
      </p>
    );
  }

  if (previewAchievement) {
    return (
      <div className="h-full min-h-0 flex-1">
        <PreviewAchievementCard
          previewAchievement={previewAchievement}
          language={language}
          size={compact ? "compact" : "default"}
          surface={embedded ? "ghost" : "default"}
          variant="landing"
          showInfoControls={!embedded}
        />
      </div>
    );
  }

  if (showEmpty) {
    return (
      <p className="rounded-xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-2.5 text-xs text-[color:var(--color-text-muted)]">
        {language === "es"
          ? "Aún no hay datos suficientes de desarrollo del hábito para esta tarea."
          : "There is not enough habit development data for this task yet."}
      </p>
    );
  }

  return null;
}

function NotAchievedPreviewOverlay({
  habit,
  language,
  disableRemote,
  mockPreviewAchievementByTaskId,
  demoAnchors,
  compact = false,
  onClose,
}: {
  habit: HabitAchievementShelfItem | null;
  language: "es" | "en";
  disableRemote: boolean;
  mockPreviewAchievementByTaskId?: Record<
    string,
    NonNullable<TaskInsightsResponse["previewAchievement"]>
  >;
  demoAnchors?: RewardsSectionProps["demoAnchors"];
  compact?: boolean;
  onClose: () => void;
}) {
  const taskId = habit?.taskId;
  const mockPreviewAchievement = taskId
    ? mockPreviewAchievementByTaskId?.[taskId]
    : null;
  const { data, status, error } = useRequest(
    () => getTaskInsights(taskId ?? ""),
    [taskId],
    {
      enabled: Boolean(taskId) && !disableRemote && !mockPreviewAchievement,
      cacheKey: taskId && !disableRemote && !mockPreviewAchievement ? `task-insights:${taskId}` : null,
      staleMs: 5 * 60 * 1000,
    },
  );

  useEffect(() => {
    if (!habit) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [habit, onClose]);

  if (!habit || typeof document === "undefined") {
    return null;
  }

  const previewAchievement =
    mockPreviewAchievement ?? data?.previewAchievement ?? null;
  const isLocalPreview = Boolean(mockPreviewAchievement);

  return createPortal(
    <div
      className="fixed inset-0 z-[230] flex items-end justify-center bg-slate-950/70 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:items-center"
      data-achievement-overlay="seal-path"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm overflow-y-auto rounded-3xl border border-[color:var(--color-border-soft)] bg-[color:var(--color-surface-elevated)] p-4 max-h-[calc(100vh-2rem)]"
        onClick={(event) => event.stopPropagation()}
        data-demo-anchor={demoAnchors?.sealPath}
      >
        <button
          type="button"
          onClick={onClose}
          data-achievement-action="close-overlay"
          className="absolute right-4 top-4 rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--color-surface)] px-2 py-1 text-xs text-[color:var(--color-text)]"
          aria-label={
            language === "es" ? "Cerrar vista de sello" : "Close seal preview"
          }
        >
          ✕
        </button>
        <div className="pr-9">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[color:var(--color-text-dim)]">
            {language === "es" ? "Ruta del sello" : "Seal path"}
          </p>
          <h3 className="mt-1 text-base font-semibold text-[color:var(--color-text-strong)]">
            {habit.taskName}
          </h3>
        </div>

        <div className="mt-4">
          {status === "loading" && !isLocalPreview ? (
            <div className="rounded-2xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
              {language === "es"
                ? "Cargando vista previa del logro…"
                : "Loading achievement preview…"}
            </div>
          ) : null}
          {status === "error" ? (
            <div className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-100">
              {error?.message ??
                (language === "es"
                  ? "No pudimos cargar el estado del sello."
                  : "Couldn't load seal state.")}
            </div>
          ) : null}
          {(status === "success" || isLocalPreview) && previewAchievement ? (
            <PreviewAchievementCard
              previewAchievement={previewAchievement}
              language={language}
              size={compact ? "compact" : "default"}
              variant="landing"
            />
          ) : null}
          {(status === "success" || isLocalPreview) && !previewAchievement ? (
            <div className="rounded-2xl border border-dashed border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] p-4 text-sm text-[color:var(--color-text-muted)]">
              {language === "es"
                ? "Sigue registrando esta tarea para desbloquear el sello."
                : "Keep logging this task to unlock the seal."}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AchievementFocusOverlay({
  habit,
  language,
  showBackFace,
  disableRemote,
  demoAnchors,
  onFlip,
  onClose,
  onToggleMaintainedWithPending,
  maintainPendingHabitId,
}: {
  habit: HabitAchievementShelfItem | null;
  language: "es" | "en";
  showBackFace: boolean;
  disableRemote: boolean;
  demoAnchors?: RewardsSectionProps["demoAnchors"];
  onFlip: () => void;
  onClose: () => void;
  onToggleMaintainedWithPending: (
    habit: HabitAchievementShelfItem,
    enabled: boolean,
  ) => Promise<void>;
  maintainPendingHabitId: string | null;
}) {
  useEffect(() => {
    if (!habit) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [habit, onClose]);

  if (!habit || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[230] flex items-end justify-center bg-slate-950/70 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:items-center"
      data-achievement-overlay="focus-card"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-achievement-action="close-overlay"
          className="absolute -top-3 right-0 z-10 rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--color-surface)] px-2 py-1 text-xs text-[color:var(--color-text)]"
          aria-label={
            language === "es"
              ? "Cerrar logro activo"
              : "Close active achievement"
          }
        >
          ✕
        </button>
        <button
          type="button"
          onClick={onFlip}
          data-achievement-action="flip-card"
          data-demo-anchor={demoAnchors?.achievementCard}
          className="ib-card-contour-shadow flex h-[min(82vh,34rem)] min-h-[24rem] w-full flex-col rounded-3xl border border-[color:var(--color-border-soft)] bg-[color:var(--color-surface-elevated)] p-5 text-left"
        >
          {!showBackFace ? (
            <div
              className="flex h-full flex-col items-center justify-center gap-4 text-center"
              data-demo-anchor={demoAnchors?.achievementFront}
            >
              <div className="flex h-[min(60vw,18rem)] min-h-44 w-[min(60vw,18rem)] min-w-44 items-center justify-center rounded-full border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] text-7xl shadow-[0_20px_50px_rgba(0,0,0,0.24)] sm:h-[75%] sm:max-h-72 sm:min-h-56 sm:w-[75%] sm:max-w-72 sm:min-w-56 sm:text-8xl">
                <HabitAchievementSeal
                  pillar={habit.pillar}
                  traitCode={habit.trait?.code}
                  traitName={habit.trait?.name}
                  alt={`${habit.taskName} seal`}
                  className="h-full w-full overflow-hidden rounded-full"
                  imgClassName="h-full w-full object-cover"
                  fallback={
                    <span className="leading-none">
                      {habit.seal.visible ? "🏅" : "✨"}
                    </span>
                  }
                />
              </div>
              <p className="text-base font-semibold text-[color:var(--color-text-strong)] sm:text-lg">
                {habit.taskName}
              </p>
              <p className="text-sm text-[color:var(--color-text-muted)]">
                {language === "es"
                  ? "Toque nuevamente para ver el reverso"
                  : "Tap again to view the back side"}
              </p>
            </div>
          ) : (
            <div
              className="flex h-full flex-col items-center justify-center gap-3 text-center"
              data-demo-anchor={demoAnchors?.achievementBack}
            >
              <AchievedHabitBackContent
                habit={habit}
                language={language}
                disableRemote={disableRemote}
                maintainPendingHabitId={maintainPendingHabitId}
                onToggleMaintainedWithPending={onToggleMaintainedWithPending}
                centerAligned
              />
              <p className="text-xs text-[color:var(--color-text-dim)]">
                {language === "es"
                  ? "Toque nuevamente para volver al frente"
                  : "Tap again to return to the front"}
              </p>
            </div>
          )}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function AchievedHabitBackContent({
  habit,
  language,
  disableRemote,
  maintainPendingHabitId,
  onToggleMaintainedWithPending,
  centerAligned = false,
  compact = false,
}: {
  habit: HabitAchievementShelfItem;
  language: "es" | "en";
  disableRemote: boolean;
  maintainPendingHabitId: string | null;
  onToggleMaintainedWithPending: (
    habit: HabitAchievementShelfItem,
    enabled: boolean,
  ) => Promise<void>;
  centerAligned?: boolean;
  compact?: boolean;
}) {
  const traitLabel =
    habit.trait?.name ||
    habit.trait?.code ||
    (language === "es" ? "Sin rasgo visible" : "No visible trait");

  if (compact) {
    const achievedDate = habit.achievedAt?.slice(0, 10) ?? "—";
    return (
      <div className="flex h-full w-full flex-col justify-center gap-3">
        <div className="space-y-2 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-600 dark:text-amber-300">
            {language === "es" ? "Logro desbloqueado" : "Achievement unlocked"}
          </p>
          <p className="text-xl font-semibold leading-tight text-[color:var(--color-text-strong)]">
            {habit.taskName}
          </p>
          <p className="text-base leading-tight text-[color:var(--color-text-muted)]">
            {traitLabel}
          </p>
        </div>
        <dl className="mt-2 grid gap-0 text-sm">
          <div className="flex items-center justify-between gap-4 border-t border-[color:var(--color-border-subtle)] py-2">
            <dt className="text-[color:var(--color-text-muted)]">
              {language === "es" ? "Logrado" : "Achieved"}
            </dt>
            <dd className="font-semibold text-[color:var(--color-text-strong)]">
              {achievedDate}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-[color:var(--color-border-subtle)] py-2">
            <dt className="text-[color:var(--color-text-muted)]">
              {language === "es" ? "GP antes" : "GP before"}
            </dt>
            <dd className="font-semibold text-[color:var(--color-text-strong)]">
              {habit.gpBeforeAchievement} GP
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-[color:var(--color-border-subtle)] py-2">
            <dt className="text-[color:var(--color-text-muted)]">
              {language === "es" ? "GP mantenido" : "Maintained GP"}
            </dt>
            <dd className="font-semibold text-[color:var(--color-text-strong)]">
              {habit.gpSinceMaintain} GP
            </dd>
          </div>
        </dl>
        <div className="border-t border-[color:var(--color-border-subtle)] pt-2">
          <MaintainToggleRow
            language={language}
            checked={habit.maintainEnabled}
            disabled={disableRemote || maintainPendingHabitId === habit.id}
            onToggle={() => {
              void onToggleMaintainedWithPending(habit, !habit.maintainEnabled);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex w-full flex-col gap-1.5 ${centerAligned ? "items-center text-center" : ""}`}
    >
      <p className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--color-text-dim)]">
        {language === "es" ? "Logro desbloqueado" : "Achievement unlocked"}
      </p>
      <p className="text-lg font-semibold leading-tight text-[color:var(--color-text-strong)]">
        {habit.taskName}
      </p>
      <p className="text-sm leading-tight text-[color:var(--color-text-muted)]">
        {traitLabel}
      </p>
      <p className="text-xs text-[color:var(--color-text-muted)]">
        {language === "es" ? "Logrado el" : "Achieved on"}{" "}
        {habit.achievedAt?.slice(0, 10) ?? "—"}
      </p>
      <p className="text-xs text-[color:var(--color-text-muted)]">
        {language === "es" ? "GP antes del logro" : "GP before achievement"}:{" "}
        {habit.gpBeforeAchievement}
      </p>
      <p className="text-xs text-[color:var(--color-text-muted)]">
        {language === "es"
          ? "GP desde mantener activo"
          : "GP since keep maintained"}
        : {habit.gpSinceMaintain}
      </p>
      <div
        className={`mt-0.5 rounded-xl border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] px-2.5 py-1.5 ${centerAligned ? "w-full max-w-sm" : ""}`}
      >
        <MaintainToggleRow
          language={language}
          checked={habit.maintainEnabled}
          disabled={disableRemote || maintainPendingHabitId === habit.id}
          onToggle={() => {
            void onToggleMaintainedWithPending(habit, !habit.maintainEnabled);
          }}
        />
      </div>
    </div>
  );
}

function MaintainToggleRow({
  language,
  checked,
  disabled,
  onToggle,
}: {
  language: "es" | "en";
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm text-[color:var(--color-text)]">
      <span>{language === "es" ? "Mantener activo" : "Keep maintained"}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={language === "es" ? "Mantener activo" : "Keep maintained"}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full border transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-65 ${
          checked
            ? "border-emerald-300/70 bg-emerald-500/80"
            : "border-[color:var(--color-border-strong)] bg-[color:var(--color-overlay-3)]"
        }`}
      >
        <span
          className={`block h-6 w-6 rounded-full bg-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] transition-transform duration-300 ${checked ? "translate-x-7" : "translate-x-1"}`}
        />
      </button>
    </label>
  );
}

function DecisionModal({
  habit,
  language,
  total,
  currentIndex,
  transitioning,
  onClose,
  onMaintain,
  onStore,
}: {
  habit: HabitAchievementShelfItem;
  language: "es" | "en";
  total: number;
  currentIndex: number;
  transitioning: boolean;
  onClose: () => void;
  onMaintain: () => void;
  onStore: () => void;
}) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[240] flex items-center justify-center bg-slate-950/85 p-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
      onClick={onClose}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={habit.id}
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{
            opacity: transitioning ? 0.55 : 1,
            y: 0,
            scale: transitioning ? 0.985 : 1,
          }}
          exit={{ opacity: 0, y: -12, scale: 0.98 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="w-full max-w-3xl rounded-3xl border border-[color:var(--color-border-subtle)] bg-[color:var(--color-surface)] p-4 sm:p-6"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="mb-4 flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--color-text-dim)]">
              {language === "es" ? "Hábito logrado" : "Achieved habit"}{" "}
              {currentIndex + 1}/{total}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-[color:var(--color-border-subtle)] px-2 py-1 text-xs"
            >
              ✕
            </button>
          </div>
          <p className="mb-4 text-lg font-semibold">{habit.taskName}</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-emerald-300/50 bg-emerald-400/10 p-4">
              <p className="font-semibold">
                {language === "es" ? "Mantener hábito" : "Maintain habit"}
              </p>
              <ul className="mt-2 space-y-1 text-sm text-[color:var(--color-text-muted)]">
                <li>
                  •{" "}
                  {language === "es"
                    ? "Vuelve a Daily Quest"
                    : "Returns to Daily Quest"}
                </li>
                <li>
                  •{" "}
                  {language === "es"
                    ? "Sello visible en tareas activas"
                    : "Seal shown in active tasks"}
                </li>
                <li>
                  • {language === "es" ? "Genera 1 GP" : "Generates 1 GP"}
                </li>
              </ul>
              <button
                type="button"
                onClick={onMaintain}
                className="mt-3 w-full rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white"
              >
                {language === "es" ? "Mantener hábito" : "Maintain habit"}
              </button>
            </div>
            <div className="rounded-2xl border border-violet-300/40 bg-violet-500/10 p-4">
              <p className="font-semibold">
                {language === "es"
                  ? "Guardar en Logros"
                  : "Store in Achievements"}
              </p>
              <ul className="mt-2 space-y-1 text-sm text-[color:var(--color-text-muted)]">
                <li>
                  •{" "}
                  {language === "es"
                    ? "Sale de seguimiento activo"
                    : "Leaves active tracking"}
                </li>
                <li>
                  •{" "}
                  {language === "es"
                    ? "Permanece en la estantería"
                    : "Stays in shelf forever"}
                </li>
                <li>
                  •{" "}
                  {language === "es"
                    ? "Sin generación de GP"
                    : "No GP generation"}
                </li>
              </ul>
              <button
                type="button"
                onClick={onStore}
                className="mt-3 w-full rounded-full border border-violet-300/50 bg-violet-500/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white"
              >
                {language === "es"
                  ? "Guardar en Logros"
                  : "Store in Achievements"}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
}

function CelebrationOverlay({
  habits,
  language,
  onSkip,
}: {
  habits: HabitAchievementShelfItem[];
  language: "es" | "en";
  onSkip: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-slate-950/85">
      <button
        type="button"
        onClick={onSkip}
        className="absolute right-5 top-5 rounded-full border border-white/30 px-3 py-1 text-xs text-white"
      >
        ✕
      </button>
      <p className="text-sm uppercase tracking-[0.2em] text-amber-100">
        {language === "es" ? "Celebrando" : "Celebrating"}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {habits.map((habit) => (
          <div
            key={habit.id}
            className="rounded-full border border-amber-300/40 bg-amber-400/10 px-4 py-2 text-white"
          >
            🏅 {habit.taskName}
          </div>
        ))}
      </div>
      <p className="text-xs text-[color:var(--color-slate-300)]">
        {language === "es"
          ? "Tus sellos ahora viven en tus estantes de Logros"
          : "Your seals now live in your Achievement Shelves"}
      </p>
    </div>
  );
}
